import type { Vec2, Ring } from '../../geo/types';

/** A stretch of a street frame from s = `from` to s = `to`, between t = `bottom` and t = `top`. */
export interface FrameSpan {
  readonly from: number;
  readonly to: number;
  readonly bottom: number;
  readonly top: number;
}

/**
 * Metres along a street's centre line (s) and to its left (t). Each leg of the centre line maps
 * bilinearly between the rungs at its ends: the bisector at a bend, scaled so that t is the distance
 * from both legs, and the leg's normal at the two ends, beyond which the end legs run on straight.
 * Lines of equal s are straight across each leg, and lines of equal t run parallel to the legs, so a
 * run of spans outlines as a ring through the centre line's vertices within it.
 */
export interface StreetFrame {
  /** The centre line the frame runs along: the street's, less the vertices of any leg too short for the reach. */
  readonly line: readonly Vec2[];
  readonly length: number;
  readonly reach: number;
  /** The s of each vertex between the line's ends, where one leg gives way to the next. */
  readonly bends: readonly number[];
  at(s: number, t: number): Vec2;
  /** The s and t of a layout point, on the stretch of s in `near` where the street passes a point twice. */
  coordsOf(p: Vec2, near?: readonly [number, number]): Vec2;
  /** The outline of spans that each start where the one before ends, anticlockwise. */
  outline(spans: readonly FrameSpan[]): Ring;
}

interface Leg {
  readonly a: Vec2;
  readonly e: Vec2;
  readonly n: Vec2;
  readonly length: number;
  readonly start: number;
  readonly rungA: Vec2;
  readonly rungB: Vec2;
}

// A leg keeps this share of its length on the side where its rungs close in, at the frame's reach.
const OPEN_SHARE = 0.05;
const SAME_POINT_M = 1e-9;
// A point on the line of equal s between two legs lies on both, to within rounding.
const EDGE_F = 1e-9;

function cross(a: Vec2, b: Vec2): number {
  return a[0] * b[1] - a[1] * b[0];
}

function dot(a: Vec2, b: Vec2): number {
  return a[0] * b[0] + a[1] * b[1];
}

function withoutRepeats(line: readonly Vec2[]): Vec2[] {
  return line.filter(
    (p, i) => i === 0 || Math.hypot(p[0] - line[i - 1]![0], p[1] - line[i - 1]![1]) > SAME_POINT_M
  );
}

function leftNormal(a: Vec2, b: Vec2): Vec2 {
  const length = Math.hypot(b[0] - a[0], b[1] - a[1]);
  return [-(b[1] - a[1]) / length, (b[0] - a[0]) / length];
}

// The bisector scaled to reach distance 1 from both legs; a turn right back has none.
function rungsOf(line: readonly Vec2[]): readonly (Vec2 | null)[] {
  const normals = line.slice(1).map((b, i) => leftNormal(line[i]!, b));
  return line.map((_, i) => {
    if (i === 0) return normals[0]!;
    if (i === line.length - 1) return normals[i - 1]!;
    const [p, q] = [normals[i - 1]!, normals[i]!];
    const scale = 1 + dot(p, q);
    return scale < 1e-6 ? null : [(p[0] + q[0]) / scale, (p[1] + q[1]) / scale];
  });
}

/* Along a leg, a line of equal t has length L + t k, with k the rungs' closing along the leg; it
   must stay open to the reach on both sides, or two lines of equal s cross within it. */
function foldingLeg(line: readonly Vec2[], reach: number): number {
  const rungs = rungsOf(line);
  for (let i = 0; i + 1 < line.length; i++) {
    const [ra, rb] = [rungs[i], rungs[i + 1]];
    if (!ra || !rb) return i;
    const [a, b] = [line[i]!, line[i + 1]!];
    const length = Math.hypot(b[0] - a[0], b[1] - a[1]);
    const e: Vec2 = [(b[0] - a[0]) / length, (b[1] - a[1]) / length];
    const closing = Math.abs(dot([rb[0] - ra[0], rb[1] - ra[1]], e));
    if (length - reach * closing < OPEN_SHARE * length) return i;
  }
  return -1;
}

function linesMeet(a: Vec2, b: Vec2, c: Vec2, d: Vec2): Vec2 | null {
  const r: Vec2 = [b[0] - a[0], b[1] - a[1]];
  const s: Vec2 = [d[0] - c[0], d[1] - c[1]];
  const det = cross(r, s);
  if (Math.abs(det) < 1e-12) return null;
  const u = cross([c[0] - a[0], c[1] - a[1]], s) / det;
  return [a[0] + r[0] * u, a[1] + r[1] * u];
}

// The ends of a folding leg become the corner its neighbours' lines meet at, near the leg, or its middle.
function merged(line: readonly Vec2[], i: number): Vec2[] {
  if (i === 0) return [line[0]!, ...line.slice(2)];
  if (i === line.length - 2) return [...line.slice(0, i), line[i + 1]!];
  const [a, b] = [line[i]!, line[i + 1]!];
  const middle: Vec2 = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  const corner = linesMeet(line[i - 1]!, a, line[i + 2]!, b);
  const length = Math.hypot(b[0] - a[0], b[1] - a[1]);
  const near =
    corner !== null && Math.hypot(corner[0] - middle[0], corner[1] - middle[1]) <= length;
  return [...line.slice(0, i), near ? corner : middle, ...line.slice(i + 2)];
}

function unfolded(centerline: readonly Vec2[], reach: number): Vec2[] {
  let line = withoutRepeats(centerline);
  for (let i = foldingLeg(line, reach); i >= 0 && line.length > 2; i = foldingLeg(line, reach)) {
    line = merged(line, i);
  }
  return line;
}

function legsOf(line: readonly Vec2[]): readonly Leg[] {
  const rungs = rungsOf(line);
  let start = 0;
  return line.slice(1).map((b, i) => {
    const a = line[i]!;
    const length = Math.hypot(b[0] - a[0], b[1] - a[1]);
    const leg: Leg = {
      a,
      e: [(b[0] - a[0]) / length, (b[1] - a[1]) / length],
      n: leftNormal(a, b),
      length,
      start,
      rungA: rungs[i]!,
      rungB: rungs[i + 1]!,
    };
    start += length;
    return leg;
  });
}

function legAt(legs: readonly Leg[], s: number): number {
  let lo = 0;
  let hi = legs.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (legs[mid]!.start <= s) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

function placeOn(leg: Leg, s: number, t: number, straight: boolean): Vec2 {
  const along = s - leg.start;
  if (straight) {
    const rung = along < 0 ? leg.rungA : leg.rungB;
    return [leg.a[0] + leg.e[0] * along + rung[0] * t, leg.a[1] + leg.e[1] * along + rung[1] * t];
  }
  const f = along / leg.length;
  const rx = (1 - f) * leg.rungA[0] + f * leg.rungB[0];
  const ry = (1 - f) * leg.rungA[1] + f * leg.rungB[1];
  return [leg.a[0] + leg.e[0] * along + rx * t, leg.a[1] + leg.e[1] * along + ry * t];
}

interface Candidate {
  readonly s: number;
  readonly t: number;
  // How far outside its leg the point lies, in legs; 0 inside.
  readonly outside: number;
}

/* Where p = a + f E + t ((1 - f) rA + f rB): every rung reaches distance 1 from its leg, so t is p's
   distance from the leg's line, and crossing the equation with the rung at f leaves f linear. */
function candidateOn(leg: Leg, p: Vec2, first: boolean, last: boolean): Candidate {
  const q: Vec2 = [p[0] - leg.a[0], p[1] - leg.a[1]];
  const t = dot(q, leg.n);
  const along = dot(q, leg.e);
  if (first && along < 0) return { s: leg.start + along, t, outside: 0 };
  if (last && along > leg.length) return { s: leg.start + along, t, outside: 0 };
  const closing: Vec2 = [leg.rungB[0] - leg.rungA[0], leg.rungB[1] - leg.rungA[1]];
  const f = cross(q, leg.rungA) / (leg.length - cross(q, closing));
  const outside = f < -EDGE_F ? -f : f > 1 + EDGE_F ? f - 1 : 0;
  return { s: leg.start + f * leg.length, t, outside };
}

function better(a: Candidate, b: Candidate, near: readonly [number, number] | undefined): boolean {
  const off = (c: Candidate) =>
    near === undefined ? 0 : Math.max(0, near[0] - c.s, c.s - near[1]);
  if (a.outside !== b.outside) return a.outside < b.outside;
  if (off(a) !== off(b)) return off(a) < off(b);
  return Math.abs(a.t) < Math.abs(b.t);
}

function outlineOf(spans: readonly FrameSpan[]): Vec2[] {
  const bottom = spans.flatMap((span): Vec2[] => [
    [span.from, span.bottom],
    [span.to, span.bottom],
  ]);
  const top = [...spans].reverse().flatMap((span): Vec2[] => [
    [span.to, span.top],
    [span.from, span.top],
  ]);
  return [...bottom, ...top];
}

export function streetFrame(centerline: readonly Vec2[], reach: number): StreetFrame {
  const line = unfolded(centerline, reach);
  if (line.length < 2) throw new Error('A street frame needs a centre line of two distinct points');
  const legs = legsOf(line);
  const last = legs[legs.length - 1]!;
  const length = last.start + last.length;
  const vertexS = legs.slice(1).map((leg) => leg.start);

  function at(s: number, t: number): Vec2 {
    const i = legAt(legs, s);
    const leg = legs[i]!;
    const straight = (i === 0 && s < 0) || (i === legs.length - 1 && s > length);
    return placeOn(leg, s, t, straight);
  }

  // A point near s lies on a leg that starts or ends within the reach of it.
  function legsNear(near: readonly [number, number] | undefined): readonly number[] {
    const all = legs.map((_, i) => i);
    if (near === undefined) return all;
    const close = all.filter((i) => {
      const leg = legs[i]!;
      return leg.start <= near[1] + reach && leg.start + leg.length >= near[0] - reach;
    });
    return close.length > 0 ? close : all;
  }

  function coordsOf(p: Vec2, near?: readonly [number, number]): Vec2 {
    const [first, ...rest] = legsNear(near).map((i) =>
      candidateOn(legs[i]!, p, i === 0, i === legs.length - 1)
    );
    const found = rest.reduce((best, c) => (better(c, best, near) ? c : best), first!);
    return [found.s, found.t];
  }

  /* Lines of equal t bend at the rungs between legs, so each takes the vertices it passes; a line of
     equal s across the centre line takes the point on it, where the spans on either side meet it. */
  function passedBetween([s, t]: Vec2, [s2, t2]: Vec2): Vec2[] {
    if (s === s2) return t * t2 < 0 ? [at(s, 0)] : [];
    if (t !== t2) return [];
    const passed = vertexS.filter((v) => (v - s) * (v - s2) < 0);
    if (s2 < s) passed.reverse();
    return passed.map((v) => at(v, t));
  }

  function outline(spans: readonly FrameSpan[]): Ring {
    const corners = outlineOf(spans);
    const ring = corners.flatMap((corner, k) => [
      at(corner[0], corner[1]),
      ...passedBetween(corner, corners[(k + 1) % corners.length]!),
    ]);
    return ring.filter((p, k) => {
      const next = ring[(k + 1) % ring.length]!;
      return Math.hypot(next[0] - p[0], next[1] - p[1]) > SAME_POINT_M;
    });
  }

  return { line, length, reach, bends: vertexS, at, coordsOf, outline };
}
